import { selectStandardProps } from "@/utils/componentUtils";
import { Col, Form, Modal, Row, Select } from "antd";
import { useTranslation } from "react-i18next";
import { ModalSizes, rowGutter } from "../../../../common/constants";
import { resolveFormValidationError } from "../../../../common/utils/formUtils";
import { validations } from "../../../../common/utils/validationUtils";
import type { UUID } from "../../../../typings/global";
import { ClientRepresentative, LegalClient } from "../../types";
import { formatClientName } from "../../utils";

interface Props {
  open: boolean;
  client?: Partial<LegalClient>;
  onSelect: (clientRepresentative: ClientRepresentative) => void;
  onFormCancel: () => void;
}

const ClientRepresentativeSelectModal = ({ open, client, onSelect, onFormCancel }: Props) => {
  const { t } = useTranslation();

  const [form] = Form.useForm<{ representativeId: UUID }>();

  const handleFormSubmit = () => {
    form
      .validateFields()
      .then((values) => {
        const representative = client?.representatives?.find((r) => r.id === values.representativeId);
        if (representative) {
          onSelect(representative);
        }
      })
      .catch(resolveFormValidationError);
  };

  return (
    <Modal
      width={ModalSizes.SMALL}
      open={open}
      title={t("client.helpers.representativeSelect", { clientName: client?.aggregatedName })}
      okText={t("common.prefill")}
      cancelText={t("common.cancel")}
      afterClose={() => form.resetFields()}
      onOk={handleFormSubmit}
      onCancel={onFormCancel}
      mask={{ closable: false }}
    >
      <Form form={form} layout="vertical" name="clientRepresentativeSelectForm">
        <Row gutter={rowGutter}>
          <Col span={24}>
            <Form.Item
              name="representativeId"
              label={t("calc.helpers.representativeSelectIdentifier")}
              rules={[validations.notNull]}
            >
              <Select
                {...selectStandardProps}
                options={(client?.representatives || []).map((r) => ({
                  value: r.id,
                  label: `${formatClientName(r.representative)} | ${t(
                    "client.enums.representativeFunction." + r.function,
                  )}`,
                }))}
              />
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
};

export default ClientRepresentativeSelectModal;
